/* ═══ AgSelect — Anagogia Design System ═══ */

import { forwardRef, useId, useState } from 'react';
import styles from './AgSelect.module.css';

interface AgSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Visible label above the select */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Error state — turns border red */
  error?: boolean;
  /** Helper / error text below the select */
  helperText?: string;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
}

/**
 * Liste déroulante de sélection stylée avec les tokens ag-ui.
 * @category Formulaires
 */
const AgSelect = forwardRef<HTMLSelectElement, AgSelectProps>(
  function AgSelect(
    {
      label,
      size = 'md',
      error = false,
      helperText,
      fullWidth = false,
      className,
      disabled,
      id: idProp,
      onFocus,
      onBlur,
      children,
      ...rest
    },
    ref,
  ) {
    const autoId = useId();
    const inputId = idProp ?? autoId;
    const helperId = helperText ? `${inputId}-helper` : undefined;

    const [focused, setFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      setFocused(false);
      onBlur?.(e);
    };

    const wrapperClass = [
      styles.wrapper,
      fullWidth && styles.fullWidth,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const containerClass = [
      styles.selectContainer,
      styles[size],
      focused && styles.focused,
      error && styles.error,
      disabled && styles.disabled,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClass}>
        {label && (
          <label className={styles.label} htmlFor={inputId}>
            {label}
          </label>
        )}

        <div className={containerClass}>
          <select
            ref={ref}
            id={inputId}
            className={styles.select}
            disabled={disabled}
            aria-invalid={error || undefined}
            aria-describedby={helperId}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...rest}
          >
            {children}
          </select>

          <span className={styles.arrow}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        {helperText && (
          <span
            id={helperId}
            className={[
              styles.helperText,
              error && styles.helperTextError,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {helperText}
          </span>
        )}
      </div>
    );
  },
);

export { AgSelect };
export type { AgSelectProps };
export default AgSelect;
